import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Sparkles, TrendingUp, BookOpen, Copy,
  RefreshCw, Send, Linkedin, Instagram, Facebook, ChevronRight, Lightbulb
} from 'lucide-react';
import HashtagSuggest from '@/components/social/HashtagSuggest';
import ImageGenerator from '@/components/social/ImageGenerator';

const API = '/api';

const BLOG_TYPES = [
  { value: 'thought_leadership', label: 'Thought Leadership', desc: 'Industry trends & expert opinion' },
  { value: 'product_spotlight', label: 'Product Spotlight', desc: 'Deep-dive into features & use cases' },
  { value: 'how_to', label: 'How-To Guide', desc: 'Practical tips & maintenance guides' },
  { value: 'industry_insight', label: 'Industry Insight', desc: 'Sector-specific content' },
  { value: 'customer_success', label: 'Customer Success', desc: 'Delivery stories & case studies' },
];

function TopicCard({ topic, onSelect }) {
  const catColor = {
    thought_leadership: 'bg-indigo-100 text-indigo-700',
    product_spotlight: 'bg-emerald-100 text-emerald-700',
    how_to: 'bg-amber-100 text-amber-700',
    industry_insight: 'bg-blue-100 text-blue-700',
    customer_success: 'bg-pink-100 text-pink-700',
  };
  return (
    <button
      onClick={() => onSelect(topic)}
      className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all group"
      data-testid={`topic-${topic.category}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={`${catColor[topic.category] || 'bg-slate-100'} text-[10px]`}>{topic.category?.replace('_', ' ')}</Badge>
            <span className="text-[10px] text-slate-400">{topic.estimated_word_count || 800}+ words</span>
          </div>
          <p className="font-medium text-sm text-slate-800">{topic.title}</p>
          <p className="text-xs text-slate-500 mt-1">{topic.hook}</p>
          <div className="flex gap-1 mt-2 flex-wrap">
            {(topic.keywords || []).slice(0, 4).map(k => (
              <span key={k} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">{k}</span>
            ))}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-500 mt-1 shrink-0 transition-colors" />
      </div>
    </button>
  );
}

export default function BlogGenerator() {
  const navigate = useNavigate();
  const [step, setStep] = useState('discover'); // discover | configure | generate | result
  const [discovering, setDiscovering] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [topics, setTopics] = useState([]);
  const [focusArea, setFocusArea] = useState('');
  const [config, setConfig] = useState({
    topic: '', target_audience: '', tone: 'professional',
    word_count: 800, keywords: '', blog_type: 'thought_leadership', include_cta: true,
  });
  const [result, setResult] = useState(null);
  const [refineInput, setRefineInput] = useState('');
  const [socialTab, setSocialTab] = useState('linkedin');

  const discoverTopics = async () => {
    setDiscovering(true);
    try {
      const { data } = await axios.post(`${API}/social/blog/discover-topics`, { focus_area: focusArea }, { withCredentials: true });
      setTopics(data.topics || []);
      if ((data.topics || []).length === 0) toast.info('No topics found — try a different focus area');
    } catch (e) {
      toast.error('Topic discovery failed');
    } finally {
      setDiscovering(false);
    }
  };

  const selectTopic = (topic) => {
    setConfig(prev => ({
      ...prev,
      topic: topic.title,
      target_audience: topic.target_audience || '',
      keywords: (topic.keywords || []).join(', '),
      blog_type: topic.category || 'thought_leadership',
      word_count: topic.estimated_word_count || 800,
    }));
    setStep('configure');
  };

  const generateBlog = async () => {
    if (!config.topic.trim()) { toast.error('Topic is required'); return; }
    setGenerating(true);
    setStep('generate');
    try {
      const { data } = await axios.post(`${API}/social/blog/generate`, {
        ...config,
        keywords: config.keywords.split(',').map(k => k.trim()).filter(Boolean),
      }, { withCredentials: true });
      setResult(data);
      setStep('result');
    } catch (e) {
      toast.error('Blog generation failed');
      setStep('configure');
    } finally {
      setGenerating(false);
    }
  };

  const refineBlog = async () => {
    if (!refineInput.trim()) return;
    setRefining(true);
    try {
      const { data } = await axios.post(`${API}/social/blog/refine`, {
        current_blog: result?.blog?.content || '',
        instruction: refineInput,
      }, { withCredentials: true });
      setResult(data);
      setRefineInput('');
      toast.success('Blog refined!');
    } catch (e) {
      toast.error('Refinement failed');
    } finally {
      setRefining(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const saveAsContent = async () => {
    if (!result?.blog) return;
    try {
      const formData = new FormData();
      formData.append('content_type', 'blog');
      formData.append('platforms', JSON.stringify(['website', 'linkedin']));
      formData.append('blog_title', result.blog.title || config.topic);
      formData.append('blog_content', result.blog.content || '');
      formData.append('caption', result.social_snippets?.linkedin || '');
      formData.append('hashtags', (result.blog.keywords_used || []).map(k => `#${k.replace(/\s/g, '')}`).join(' '));
      formData.append('purpose', `AI-generated blog: ${config.topic}`);
      formData.append('ai_generated', 'true');
      await axios.post(`${API}/social/content/upload`, formData, { withCredentials: true });
      toast.success('Blog saved as content for review!');
      navigate('/social');
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const blog = result?.blog;
  const snippets = result?.social_snippets;

  return (
    <div className="max-w-4xl mx-auto space-y-5 p-4 md:p-6" data-testid="blog-generator-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/social')} className="text-slate-500">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">AI Blog Generator</h1>
        <p className="text-sm text-slate-500 mt-1">Discover trending topics, generate SEO-optimized blogs, and auto-create social snippets</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 text-xs">
        {['discover', 'configure', 'generate', 'result'].map((s, i) => (
          <div key={s} className="flex items-center gap-1.5">
            {i > 0 && <div className="w-6 h-px bg-slate-200" />}
            <div className={`px-2.5 py-1 rounded-full font-medium ${step === s ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
              {i + 1}. {s === 'discover' ? 'Topics' : s === 'configure' ? 'Configure' : s === 'generate' ? 'Generating' : 'Result'}
            </div>
          </div>
        ))}
      </div>

      {/* STEP 1: Discover Topics */}
      {step === 'discover' && (
        <Card className="border-slate-200" data-testid="discover-step">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-indigo-500" /> Discover Trending Topics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={focusArea}
                onChange={e => setFocusArea(e.target.value)}
                placeholder="Optional focus: telecom, construction, maintenance..."
                className="flex-1"
                data-testid="focus-input"
              />
              <Button onClick={discoverTopics} disabled={discovering} data-testid="discover-btn">
                {discovering ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                Discover
              </Button>
            </div>
            <p className="text-xs text-slate-400">AI researches trending topics in diesel generators, power solutions, and your target industries</p>

            {topics.length > 0 && (
              <div className="space-y-2 pt-2">
                {topics.map((t, i) => <TopicCard key={i} topic={t} onSelect={selectTopic} />)}
              </div>
            )}

            <div className="pt-2 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={() => { setConfig(prev => ({ ...prev, topic: '' })); setStep('configure'); }}>
                Or write your own topic
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2: Configure */}
      {step === 'configure' && (
        <Card className="border-slate-200" data-testid="configure-step">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" /> Configure Blog
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Topic / Title</Label>
              <Textarea
                value={config.topic}
                onChange={e => setConfig(prev => ({ ...prev, topic: e.target.value }))}
                placeholder="e.g., Why CPCB IV+ Compliance Matters for Your Diesel Generator"
                rows={2}
                data-testid="topic-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Blog Type</Label>
                <Select value={config.blog_type} onValueChange={v => setConfig(prev => ({ ...prev, blog_type: v }))}>
                  <SelectTrigger data-testid="type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BLOG_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tone</Label>
                <Select value={config.tone} onValueChange={v => setConfig(prev => ({ ...prev, tone: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="conversational">Conversational</SelectItem>
                    <SelectItem value="authoritative">Authoritative</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Target Audience</Label>
              <Input
                value={config.target_audience}
                onChange={e => setConfig(prev => ({ ...prev, target_audience: e.target.value }))}
                placeholder="B2B industrial buyers, telecom operators, facility managers..."
                data-testid="audience-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SEO Keywords (comma separated)</Label>
                <Input
                  value={config.keywords}
                  onChange={e => setConfig(prev => ({ ...prev, keywords: e.target.value }))}
                  placeholder="diesel generator, 125 kVA, CPCB IV+"
                />
              </div>
              <div className="space-y-2">
                <Label>Word Count</Label>
                <Select value={String(config.word_count)} onValueChange={v => setConfig(prev => ({ ...prev, word_count: Number(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="500">500 words (Short)</SelectItem>
                    <SelectItem value="800">800 words (Standard)</SelectItem>
                    <SelectItem value="1200">1200 words (Long)</SelectItem>
                    <SelectItem value="1500">1500+ words (Comprehensive)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={generateBlog} disabled={!config.topic.trim()} data-testid="generate-btn">
                <BookOpen className="h-4 w-4 mr-1" /> Generate Blog
              </Button>
              <Button variant="ghost" onClick={() => setStep('discover')}>Back to Topics</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 3: Generating */}
      {step === 'generate' && (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-indigo-500 mx-auto" />
            <p className="text-lg font-medium text-slate-700 mt-4">Generating your blog...</p>
            <p className="text-sm text-slate-400 mt-1">Researching trends, writing content, optimizing SEO, creating social snippets</p>
          </CardContent>
        </Card>
      )}

      {/* STEP 4: Result */}
      {step === 'result' && blog && (
        <>
          {/* Blog Preview */}
          <Card className="border-slate-200" data-testid="blog-result">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{blog.title}</CardTitle>
                  {blog.meta_description && (
                    <p className="text-xs text-slate-400 mt-1">{blog.meta_description}</p>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => copyText(blog.content)}>
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                  </Button>
                  <Button size="sm" onClick={saveAsContent} data-testid="save-content-btn">
                    <Send className="h-3.5 w-3.5 mr-1" /> Save for Review
                  </Button>
                </div>
              </div>
              <div className="flex gap-2 mt-2 flex-wrap">
                {(blog.keywords_used || []).map(k => (
                  <Badge key={k} variant="outline" className="text-[10px]">{k}</Badge>
                ))}
                <Badge className="bg-slate-100 text-slate-600 text-[10px]">{blog.word_count || '?'} words</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none text-slate-700 max-h-[500px] overflow-y-auto p-4 bg-slate-50 rounded-lg border border-slate-100" data-testid="blog-content-preview">
                {(blog.content || '').split('\n').map((line, i) => {
                  if (line.startsWith('### ')) return <h3 key={i} className="text-base font-bold mt-4 mb-1">{line.replace('### ', '')}</h3>;
                  if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold mt-5 mb-2">{line.replace('## ', '')}</h2>;
                  if (line.startsWith('# ')) return <h1 key={i} className="text-xl font-bold mt-6 mb-2">{line.replace('# ', '')}</h1>;
                  if (line.startsWith('- ')) return <li key={i} className="ml-4">{line.replace('- ', '')}</li>;
                  if (line.trim() === '') return <br key={i} />;
                  return <p key={i} className="mb-2">{line}</p>;
                })}
              </div>

              {blog.image_suggestions?.length > 0 && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-xs font-medium text-amber-700 mb-1">Suggested Images</p>
                  {blog.image_suggestions.map((img, i) => (
                    <p key={i} className="text-xs text-amber-600">{i + 1}. {img}</p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Refine */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex gap-2">
                <Input
                  value={refineInput}
                  onChange={e => setRefineInput(e.target.value)}
                  placeholder="Refine: make it longer, add more technical details, focus on telecom..."
                  className="flex-1"
                  data-testid="refine-input"
                  onKeyDown={e => e.key === 'Enter' && refineBlog()}
                />
                <Button onClick={refineBlog} disabled={refining || !refineInput.trim()} data-testid="refine-btn">
                  {refining ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Social Snippets */}
          {snippets && (snippets.linkedin || snippets.instagram || snippets.facebook) && (
            <Card className="border-slate-200" data-testid="social-snippets">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Auto-Generated Social Snippets</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs value={socialTab} onValueChange={setSocialTab}>
                  <TabsList>
                    {snippets.linkedin && <TabsTrigger value="linkedin" className="gap-1"><Linkedin className="h-3.5 w-3.5" /> LinkedIn</TabsTrigger>}
                    {snippets.instagram && <TabsTrigger value="instagram" className="gap-1"><Instagram className="h-3.5 w-3.5" /> Instagram</TabsTrigger>}
                    {snippets.facebook && <TabsTrigger value="facebook" className="gap-1"><Facebook className="h-3.5 w-3.5" /> Facebook</TabsTrigger>}
                  </TabsList>
                  {Object.entries(snippets).filter(([_, v]) => v).map(([platform, text]) => (
                    <TabsContent key={platform} value={platform}>
                      <div className="relative">
                        <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto">{text}</div>
                        <Button variant="ghost" size="sm" className="absolute top-1 right-1" onClick={() => copyText(text)}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          )}

          {/* AI Image Generator */}
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <ImageGenerator
                onImageGenerated={(fileInfo) => {
                  if (fileInfo) toast.success('Design imported from Canva!');
                }}
              />
            </CardContent>
          </Card>

          {/* Hashtag Intelligence */}
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <HashtagSuggest
                contentText={blog?.content?.slice(0, 500)}
                topic={blog?.title || config.topic}
                platforms={['linkedin', 'instagram', 'facebook', 'twitter']}
                onApply={(tags) => toast.success(`Hashtags: ${tags}`)}
              />
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep('configure')}>Edit Config & Regenerate</Button>
            <Button variant="outline" onClick={() => { setStep('discover'); setResult(null); }}>Start New Blog</Button>
          </div>
        </>
      )}
    </div>
  );
}
